import { DateTime } from '@ksyos/date-time';
import { Alert, Button, Card, FormButtonRow, Heading } from '@ksyos/design-system';
import { useNavigate, useSearchParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import type { IPatientSearchWithBirthDateAndOtherOptions } from '@common/validation/patient';
import type { ValidationErrors } from '@frontend/utils';
import { PatientSearchOptionWithBirthDate } from '@common/enums/patientSearchOptionWithBirthDate';
import { patientSearchWithBirthDateAndOtherOptionsSchema } from '@common/validation/patient';
import { RadioGroupField } from '@frontend/components/fields/RadioGroup';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { emptyValidationErrors, updateValidationErrorsForOne, useAbortableFetch, validate } from '@frontend/utils';
import { translateKey } from '../../../../translate';
import { Form, Loading } from '../../../elements/common';
import { DateField } from '../../../fields/Date';
import { PatientSearchOtherOptionsFormFields } from './PatientSearchOtherOptionsFormFields';

export interface IPatientSearchWithBirthdateAndOtherOptionsProps {
    defaultSearchOptionWithBirthDate: PatientSearchOptionWithBirthDate;
    header: string;
    searchOptionsWithBirthDate: PatientSearchOptionWithBirthDate[];
    thirdHeader: string;
}

const initialPatientSearchData: Omit<IPatientSearchWithBirthDateAndOtherOptions, 'searchOption'> = {
    birthDate: '',
    houseNumber: '',
    identifierSocialSecurity: '',
    insurancePolicyNumber: '',
    zip: '',
};

export function PatientSearchWithBirthdateAndOtherOptions(props: IPatientSearchWithBirthdateAndOtherOptionsProps) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const ssoId = searchParams.get('ssoId');
    const birthDate = searchParams.get('birthDate');
    const abortableFetch = useAbortableFetch();
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [patientNotFound, setPatientNotFound] = useState(false);
    const [patientSearchData, setPatientSearchData] = useState<IPatientSearchWithBirthDateAndOtherOptions>({
        ...initialPatientSearchData,
        searchOption: props.defaultSearchOptionWithBirthDate,
    });

    useEffect(() => {
        loadInitialPatientSearchData();
    }, []);

    return (
        <PageTemplate width="medium">
            <Heading
                mb={8}
                size="xlarge"
            >
                {props.header}
            </Heading>
            <Loading
                item={patientSearchData}
                loaded={(loadedPatientSearchData) => (
                    <Card>
                        <Form
                            formContext={{
                                item: loadedPatientSearchData,
                                placeholderGroupName: 'patientSso',
                                errors,
                                onChange: (key, value) => handleChange(key, value),
                                onDoValidate: (key) => validateOne(key),
                            }}
                            onSubmit={() => null}
                        >
                            <Card.Header title={props.thirdHeader} />
                            <Card.Body>
                                {patientNotFound && (
                                    <Alert appearance="warning">
                                        {translateKey('patientSso.patientNotFoundInvalid')}
                                    </Alert>
                                )}
                                <DateField
                                    birthDate={true}
                                    fieldKey="birthDate"
                                    isRequired={true}
                                    label={translateKey('patient.birthDateShort')}
                                    onDoValidate={(date) => birthDateValidation(date)}
                                    size="medium"
                                />
                                <RadioGroupField
                                    fieldKey="searchOption"
                                    isRequired={true}
                                    labels={getRadioLabels()}
                                    options={props.searchOptionsWithBirthDate}
                                    radioButtonsStacked
                                >
                                    <PatientSearchOtherOptionsFormFields
                                        searchOption={loadedPatientSearchData.searchOption}
                                    />
                                </RadioGroupField>
                                <FormButtonRow>
                                    <Button
                                        onClick={() => navigate('/home')}
                                        variant="outline"
                                    >
                                        {translateKey('common.cancel')}
                                    </Button>
                                    <Button
                                        isDisabled={errors.size > 0}
                                        onClick={async () => await handleAddingPatient()}
                                        variant="solid"
                                    >
                                        {translateKey('common.proceed')}
                                    </Button>
                                </FormButtonRow>
                            </Card.Body>
                        </Form>
                    </Card>
                )}
            />
        </PageTemplate>
    );

    function birthDateValidation(date: string | null) {
        if (!date || !DateTime.from(date).isValid()) {
            setErrors(errors.set('birthDate', 'any_required'));
        } else if (DateTime.from(date).isAfter(DateTime.now(), 'day')) {
            setErrors(errors.set('birthDate', 'date_future'));
        } else {
            setErrors(errors.remove('birthDate'));
        }
    }

    function getRadioLabels() {
        const labels: string[] = [];

        for (const key of props.searchOptionsWithBirthDate) {
            switch (key) {
                case PatientSearchOptionWithBirthDate.identifierSocialSecurity:
                    labels.push(translateKey(`patientSso.radioLabel_${key}`));
                    break;
                case PatientSearchOptionWithBirthDate.insurance:
                    labels.push(translateKey(`patientSso.radioLabel_${key}`));
                    break;
                case PatientSearchOptionWithBirthDate.address:
                    labels.push(translateKey(`patientSso.radioLabel_${key}`));
                    break;
                case PatientSearchOptionWithBirthDate.other:
                    labels.push(translateKey(`patientSso.radioLabel_${key}`));
                    break;
                default:
                    throw new Error(`Invalid PatientSearchDataOption key "${key}"`);
            }
        }

        return labels;
    }

    function getSearchOption(
        identifierSocialSecurity?: string,
        insurancePolicyNumber?: string,
        houseNumber?: string,
        zip?: string
    ) {
        if (identifierSocialSecurity) {
            return PatientSearchOptionWithBirthDate.identifierSocialSecurity;
        } else if (insurancePolicyNumber) {
            return PatientSearchOptionWithBirthDate.insurance;
        } else if (houseNumber || zip) {
            return PatientSearchOptionWithBirthDate.address;
        }

        return PatientSearchOptionWithBirthDate.identifierSocialSecurity;
    }

    async function handleAddingPatient() {
        const patientSearchDataPayload = createPatientSearchDataPayload(patientSearchData);
        const {
            hasErrors,
            errors: addPatientErrors,
            item,
        } = validate(patientSearchWithBirthDateAndOtherOptionsSchema, {
            ...patientSearchDataPayload,
            extraQueryParameters: Object.fromEntries(searchParams),
        });
        if (hasErrors) {
            return setErrors(addPatientErrors);
        }
        setPatientNotFound(false);

        if (item) {
            try {
                const result = await abortableFetch.postJson('/api/patients/birthDateAndOtherOptions', item);
                navigate(result.json.redirect);
            } catch (e: any) {
                if (e.statusCode === 404) {
                    setPatientNotFound(true);
                } else {
                    throw e;
                }
            }
        }
    }

    function handleChange<K extends keyof IPatientSearchWithBirthDateAndOtherOptions>(
        key: K,
        value: IPatientSearchWithBirthDateAndOtherOptions[K]
    ) {
        if (key === 'searchOption' && errors.size > 0) {
            setErrors(
                errors
                    .remove('insurancePolicyNumber')
                    .remove('zip')
                    .remove('houseNumber')
                    .remove('identifierSocialSecurity')
            );
        }
        const cleanedValue =
            key === 'birthDate' && typeof value === 'string' ? DateTime.from(value).format('YYYY-MM-DD') : value;
        setPatientSearchData((prevPatientSearchData) => ({ ...prevPatientSearchData, [key]: cleanedValue }));
    }

    async function loadInitialPatientSearchData() {
        if (ssoId) {
            const result = await abortableFetch.getJson(`/api/thirdPartySsoData/${ssoId}`);
            const data = result.json.data.patient;

            const ssoData = {
                birthDate: birthDate || data.birthDate || '',
                identifierSocialSecurity: data.identifierSocialSecurity || '',
                houseNumber: data.houseNumber || '',
                insurancePolicyNumber: data.insurancePolicyNumber || '',
                zip: data.zip || '',
            };
            const searchOption = getSearchOption(
                ssoData.identifierSocialSecurity,
                ssoData.insurancePolicyNumber,
                ssoData.houseNumber,
                ssoData.zip
            );
            setPatientSearchData((prevPatientSearchData) => ({ ...prevPatientSearchData, ...ssoData, searchOption }));
        } else {
            const noSsoIdPatientSearchData = {
                searchOption: props.defaultSearchOptionWithBirthDate || '',
                birthDate: birthDate || '',
                identifierSocialSecurity: '',
                houseNumber: '',
                insurancePolicyNumber: '',
                zip: '',
            };
            setPatientSearchData(noSsoIdPatientSearchData);
        }
    }

    function validateOne(name: keyof IPatientSearchWithBirthDateAndOtherOptions) {
        setErrors((prevErrors) =>
            updateValidationErrorsForOne(
                patientSearchWithBirthDateAndOtherOptionsSchema,
                patientSearchData,
                prevErrors,
                name
            )
        );
    }
}

function createPatientSearchDataPayload(
    patientSearchData: IPatientSearchWithBirthDateAndOtherOptions
): IPatientSearchWithBirthDateAndOtherOptions {
    const patientSearchDataPayload: IPatientSearchWithBirthDateAndOtherOptions = {
        ...initialPatientSearchData,
        birthDate: patientSearchData.birthDate,
        searchOption: patientSearchData.searchOption,
    };
    switch (patientSearchData.searchOption) {
        case PatientSearchOptionWithBirthDate.identifierSocialSecurity:
            return {
                ...patientSearchDataPayload,
                identifierSocialSecurity: patientSearchData.identifierSocialSecurity,
            };
        case PatientSearchOptionWithBirthDate.insurance:
            return { ...patientSearchDataPayload, insurancePolicyNumber: patientSearchData.insurancePolicyNumber };
        case PatientSearchOptionWithBirthDate.address:
            return {
                ...patientSearchDataPayload,
                zip: patientSearchData.zip,
                houseNumber: patientSearchData.houseNumber,
            };
        default:
            return patientSearchDataPayload;
    }
}
